import { SyncRunError as SyncRunErrorData } from "@dashboard-v3/api";
import { Box, Button, Collapse, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import syncErrorReason from "utils/syncErrorReason";

const ACCOUNTS_PATH = "/integrations?tab=ACCOUNTS";

type Props = {
  /** Error clasificado. Ausente en runs anteriores a MXH-5737 */
  error?: SyncRunErrorData | null;
  /** Espejo plano, unica fuente para los runs viejos */
  errorCode?: string | null;
  errorDescription?: string | null;
};

/**
 * Explica por que fallo un run y, cuando se puede, que hacer al respecto.
 *
 * Antes solo se mostraba "Error": el codigo se traducia con fallback a string
 * vacio, y como casi todo terminaba en `UNKNOWN_ERROR` el usuario no tenia nada
 * (MXH-5737). Ahora el ultimo recurso es el mensaje crudo de la API, que es feo
 * pero accionable para soporte.
 */
export default function SyncRunError({
  error,
  errorCode,
  errorDescription,
}: Props) {
  const { t } = useTranslation(["datasets", "storage"]);
  const [showDetails, setShowDetails] = useState(false);

  const code = error?.code ?? errorCode;
  if (!code) return null;

  const details = error?.raw ?? errorDescription;

  const reason = syncErrorReason(t, code, details);

  return (
    <Box mt={1} data-testid="dataset-form-sync-run-error">
      <Typography
        variant="body2"
        color="textPrimary"
        data-testid="dataset-form-sync-run-error-reason"
      >
        {reason}
      </Typography>

      {error?.provider && <FailedAgainst error={error} />}

      {error?.scope === "STORAGE_ACCOUNT" && (
        <ReauthorizeAlert
          provider={error.provider}
          storageUser={error.storageUser}
        />
      )}

      {Boolean(details) && (
        <Box mt={1}>
          <Button
            size="small"
            color="primary"
            onClick={() => setShowDetails(current => !current)}
            data-testid="dataset-form-sync-run-error-details-btn"
          >
            {showDetails
              ? t("datasets:syncRuns.hideDetails", {
                  defaultValue: "Hide details",
                })
              : t("datasets:syncRuns.showDetails", {
                  defaultValue: "Show details",
                })}
          </Button>
          <Collapse in={showDetails}>
            <Box
              mt={1}
              p={1}
              bgcolor="#f5f5f5"
              borderRadius={4}
              data-testid="dataset-form-sync-run-error-details"
            >
              <Typography
                variant="caption"
                component="pre"
                style={{
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  margin: 0,
                }}
              >
                {details}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}

/**
 * Contra que cuenta y que carpeta se corrio el run que fallo.
 *
 * Sale del run y no de la config del sync a proposito: la config es mutable y lo
 * habitual es corregirla justo despues de ver el error. Leyendola, la lista
 * mostraria la ruta actual -la que anda- al lado de un run que fallo por otra.
 */
function FailedAgainst({ error }: { error: SyncRunErrorData }) {
  const { t } = useTranslation(["datasets", "storage"]);

  const providerName = t(`storage:accountItem.provider.${error.provider}`, {
    defaultValue: String(error.provider),
  });

  const folder =
    error.parentName ||
    error.parentId ||
    t("datasets:syncRuns.rootFolder", { defaultValue: "Root folder" });

  return (
    <Box mt={1} data-testid="dataset-form-sync-run-error-context">
      <Typography variant="caption" color="textSecondary" component="div">
        {t("datasets:syncRuns.failedAccount", {
          defaultValue: "Account: {{account}} ({{provider}})",
          account: error.storageUser,
          provider: providerName,
          interpolation: { escapeValue: false },
        })}
      </Typography>
      <Box display="flex" gridGap="10px" alignItems="center">
        <Typography variant="caption" color="textSecondary" component="div">
          {t("datasets:syncRuns.failedFolder", {
            defaultValue: "Folder: {{folder}}",
            folder,
            interpolation: { escapeValue: false },
          })}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * El unico error que el usuario puede resolver solo. El link va al listado de
 * cuentas y no a la autorizacion directa porque el flujo de auth cambia segun
 * el proveedor y ya vive resuelto en esa pagina.
 */
function ReauthorizeAlert({
  provider,
  storageUser,
}: Pick<SyncRunErrorData, "provider" | "storageUser">) {
  const { t } = useTranslation(["datasets", "storage"]);

  // Si falta el nombre del proveedor en locize mostramos el crudo ("BOX"), que
  // sigue siendo legible.
  const providerName = provider
    ? t(`storage:accountItem.provider.${provider}`, {
        defaultValue: String(provider),
      })
    : "";

  // Los `defaultValue` no son decorativos: si la key falta en locize, i18next
  // devuelve la key cruda y el usuario terminaria leyendo
  // "syncRuns.reauthorizeAccount", que es el mismo problema que vino a resolver
  // este ticket. Con el default al menos lee una frase en ingles.
  const message =
    storageUser && providerName
      ? t("datasets:syncRuns.reauthorizeAccount", {
          defaultValue:
            "The {{provider}} account {{account}} needs to be re-authorized",
          account: storageUser,
          provider: providerName,
          interpolation: { escapeValue: false },
        })
      : t("datasets:syncRuns.reauthorizeGeneric", {
          defaultValue: "The cloud storage account needs to be re-authorized",
        });

  return (
    <Box mt={1}>
      <Alert
        style={{ alignItems: "center" }}
        severity="warning"
        data-testid="dataset-form-sync-run-reauthorize"
        action={
          <Button
            component={RouterLink}
            to={ACCOUNTS_PATH}
            color="inherit"
            size="small"
            data-testid="dataset-form-sync-run-reauthorize-link"
          >
            {t("datasets:syncRuns.goToAccounts", {
              defaultValue: "Go to accounts",
            })}
          </Button>
        }
      >
        {message}
      </Alert>
    </Box>
  );
}
